import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Dashboard from './Components/Dashboard';
import './App.css';
import { supabase } from './supabaseClient';
import CreateModulePage from './Components/CreateModulePage';
import ModulePage from './Components/ModulePage';
import AddWordsPage from './Components/AddWordsPage';
import WordGroupPage from './Components/WordGroupPage';
import ConversationPage from './Components/ConversationPage';
import SentencePracticePage from './Components/SentencePracticePage';
import LandingPage from './Components/LandingPage';
import LoginPage from './Components/LoginPage';
import StudyAllWordsPage from './Components/StudyAllWordsPage';
import LearnUnmasteredWordsPage from './Components/LearnUnmasteredWordsPage';
import ReviewWeakWordsPage from './Components/ReviewWeakWordsPage';
import ReviewForgottenWordsPage from './Components/ReviewForgottenWordsPage';
import LearnTopicPage from './Components/LearnTopicPage';
import StorySummaryPage from './Components/StorySummaryPage';
import FlashcardModePage from './Components/FlashcardModePage';
import FlashcardsPage from './Components/FlashcardsPage';
import PrivacyPolicy from './Components/PrivacyPolicy';
import TermsOfService from './Components/TermsOfService';
import AboutPage from './Components/AboutPage';
import AuthenticatedLayout from './Components/AuthenticatedLayout';
import ProfilePage from './Components/ProfilePage';
import ClassroomsPage from './Components/ClassroomsPage';
import ViewClassPage from './Components/ViewClassPage';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { DataProvider } from './contexts/DataContext';
import AppBanner from './Components/AppBanner';
import ErrorBoundary from './Components/ErrorBoundary';
import ResetPasswordPage from './Components/ResetPasswordPage';
import ForgotPasswordPage from './Components/ForgotPasswordPage';
import SignupPage from './Components/SignupPage';
import AuthCallback from './Components/AuthCallback';
import CreatePasswordPage from './Components/CreatePasswordPage';
import MultipleChoicePracticePage from './Components/MultipleChoicePracticePage';
import OnboardingPage from './Components/OnboardingPage';
import StorePage from './Components/StorePage';
import ThankYouPage from './Components/ThankYouPage';
import SupportPage from './Components/SupportPage';
import BlogPage from './Components/BlogPage';
import ArticlePage from './Components/ArticlePage';
import DemoWordGroupPage from './Components/DemoWordGroupPage';
import DemoFlashcardsPage from './Components/DemoFlashcardsPage';
import DemoMCQPracticePage from './Components/DemoMCQPracticePage';
import DemoFIBPracticePage from './Components/DemoFIBPracticePage';
import DemoFlashcardModePage from './Components/DemoFlashcardModePage';
import DemoFlashcardsLearningPage from './Components/DemoFlashcardsLearningPage';
import DemoStoryPage from './Components/DemoStoryPage';
import HowToUsePage from './Components/HowToUse';
import SpacedRepetitionGuide from './Components/SpacedRepetitionGuide';
import GettingStartedPage from './Components/GettingStartedPage';
import fetchAdToken from './utils/fetchAdToken';
import { trackPageView } from './utils/analytics';
import { backendUrl } from './config';
import PullToRefreshWrapper from './Components/PullToRefreshWrapper';




function AppContent() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPlan, setUserPlan] = useState(null);
  const { isRTL, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const adTokenIntervalRef = useRef(null);

  // Track page views with Google Analytics
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    // --- Global login event listener (does nothing for now) ---
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        const user = session?.user;
        if (user) {
          const hasPassword = user.user_metadata?.has_password === true;
          const hasSignedIn = user.user_metadata?.has_signed_in === true;
          const currentPath = window.location.pathname;

          // If no password yet, route to create-password first
          if (!hasPassword && currentPath !== '/create-password') {
            navigate(`/create-password?email=${encodeURIComponent(user.email || '')}`);
            return;
          }

          // First-time sign-in: mark flag and go to onboarding
          if (!hasSignedIn && currentPath !== '/onboarding') {
            // Track account creation in made_accounts table
            fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://safacards.onrender.com'}/account/track-creation`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: user.email })
            }).catch(() => { });
            supabase.auth.updateUser({ data: { has_signed_in: true } });
            navigate('/onboarding');
            return;
          }
        }

        fetchAdToken().then(() => {
          if (!window.adTokenRefreshSet) {
            window.adTokenRefreshSet = true;
            adTokenIntervalRef.current = setInterval(fetchAdToken, 50 * 60 * 1000);
          }
        });

      }
    });
    //----------------------------------------------------------

    return () => {
      subscription.unsubscribe();
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  // Gracefully handle resume-from-sleep with invalid/expired refresh tokens
  useEffect(() => {
    let isMounted = true;
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const { data } = await supabase.auth.getSession();
        const currentSession = data?.session;
        if (!currentSession) return; // already logged out
        // Touch the user endpoint to ensure token is valid; if it fails with 401, sign out
        const { data: userData, error } = await supabase.auth.getUser();
        if (error?.status === 401 || error?.message?.toLowerCase().includes('invalid') || !userData?.user) {
          await supabase.auth.signOut();
          if (isMounted) setSession(null);
        }
      } catch (_e) {
        // If anything goes wrong, force a clean sign out so UI isn't stuck
        await supabase.auth.signOut();
        if (isMounted) setSession(null);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    return () => {
      isMounted = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (session) {
      fetchAdToken().then(() => {
        if (!window.adTokenRefreshSet) {
          window.adTokenRefreshSet = true;
          adTokenIntervalRef.current = setInterval(fetchAdToken, 50 * 60 * 1000);
        }
      });
    }
  }, [session]);

  // Keep-backend-warm ping: only once on first load
  useEffect(() => {
    fetch(`${backendUrl}/ping`).catch(() => { });
  }, []);


  // Global keyboard tracking
  useEffect(() => {
    let lastInteraction = 'mouse';

    const handleKeyDown = (e) => {
      // Only consider it keyboard navigation if it's Tab or Shift+Tab
      if (e.key === 'Tab') {
        lastInteraction = 'keyboard';
      }
      // Don't change interaction type for other keys like Space, Enter, etc.
    };

    const handleMouseDown = () => {
      lastInteraction = 'mouse';
    };

    const handleFocus = (e) => {
      // Only show outline if the last interaction was keyboard (Tab)
      if (lastInteraction === 'keyboard') {
        // Add class to document body for global CSS targeting
        document.body.classList.add('keyboard-user');
      } else {
        document.body.classList.remove('keyboard-user');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('focusin', handleFocus);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('focusin', handleFocus);
    };
  }, []);

  // Note: Data fetching is now handled by DataContext

  useEffect(() => {
    if (session) {
      // Fetch user plan (premium status) once on login/session change
      const fetchUserPlan = async () => {
        try {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          if (!currentSession?.user?.id) {
            setUserPlan(null);
            return;
          }
          const { data: userData, error } = await supabase
            .from('user_data')
            .select('premium_expires_at')
            .eq('id', currentSession.user.id)
            .single();
          if (error) {
            setUserPlan(null);
            return;
          }
          const isPremium = userData?.premium_expires_at && new Date(userData.premium_expires_at) > new Date();
          setUserPlan({
            isPremium,
            premiumExpiresAt: userData?.premium_expires_at,
            plan: isPremium ? 'pro' : 'free'
          });
        } catch (error) {
          setUserPlan(null);
        }
      };
      fetchUserPlan();
    } else {
      setUserPlan(null);
    }
  }, [session]);

  const deleteModule = async (moduleId) => {
    try {
      // First, get the module to find its position and classroom_id
      const { data: moduleToDelete, error: fetchError } = await supabase
        .from('modules_new')
        .select('position, classroom_id')
        .eq('id', moduleId)
        .single();

      if (fetchError) {
        console.error('Error fetching module for deletion:', fetchError);
        return t('errorDeletingModule');
      }

      const modulePosition = moduleToDelete?.position;
      const classroomId = moduleToDelete?.classroom_id;

      // Delete the module from modules_new
      const { error } = await supabase.from('modules_new').delete().eq('id', moduleId);
      if (error) {
        return t('errorDeletingModule');
      }

      // Reorder remaining modules to fill the gap (only for private modules or within same classroom)
      if (typeof modulePosition === 'number') {
        const query = supabase
          .from('modules_new')
          .select('*')
          .gt('position', modulePosition)
          .order('position', { ascending: true });

        // Filter by classroom_id if it's a classroom module, or filter for private modules
        if (classroomId) {
          query.eq('classroom_id', classroomId);
        } else {
          query.is('classroom_id', null);
        }

        const { data: remainingModules, error: reorderError } = await query;

        if (reorderError) {
          console.error('Error fetching remaining modules for reordering:', reorderError);
        } else if (remainingModules && remainingModules.length > 0) {
          // Decrement positions of all modules with higher positions
          const reorderPromises = remainingModules.map(module =>
            supabase
              .from('modules_new')
              .update({ position: module.position - 1 })
              .eq('id', module.id)
          );

          await Promise.all(reorderPromises);
        }
      }

      // If this is a classroom module, refresh classrooms list
      if (classroomId) {
        window.dispatchEvent(new Event('refreshClassrooms'));
      }

      // The DataContext will automatically refetch modules when the component re-renders
      // No need to manually refresh since the DataContext handles this
      return null;
    } catch (error) {
      console.error('Error in deleteModule:', error);
      return t('errorDeletingModule');
    }
  };



  // Function to fetch modules with word groups (but not words) for module pages






  // Apply RTL styles to document
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = session?.user?.user_metadata?.preferred_language || 'en';
  }, [isRTL, session]);

  // Enforce onboarding only after password step is complete (if required)
  useEffect(() => {
    const user = session?.user;
    if (!user) return;
    const hasPassword = user.user_metadata?.has_password === true;
    const hasSignedIn = user.user_metadata?.has_signed_in === true;
    const path = window.location.pathname;
    if (!hasPassword && path !== '/create-password') {
      navigate(`/create-password?email=${encodeURIComponent(user.email || '')}`, { replace: true });
      return;
    }
    if (!hasSignedIn && path !== '/onboarding' && path !== '/create-password') {
      navigate('/onboarding', { replace: true });
    }
  }, [session, navigate]);

  if (loading) {
    return <div>Loading...</div>; // Or a spinner component
  }



  // Logout handler for AppBanner
  const handleLogout = async () => {
    try {

      if (adTokenIntervalRef.current) {
        clearInterval(adTokenIntervalRef.current);
        window.adTokenRefreshSet = false;
        adTokenIntervalRef.current = null;
      }
      window.userAdToken = null;

      const { error } = await supabase.auth.signOut();
      if (error) {
        alert(t('logoutError') + ': ' + error.message);
      } else {
        // Force a page reload to clear any cached state
        window.location.reload();
      }
    } catch (err) {
      alert(t('logoutError') + ': ' + err.message);
    }
  };

  return (
    <div className="App">
      <Routes>
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/create-password" element={<CreatePasswordPage />} />
        <Route path="*" element={
          <>
            <AppBanner key={session?.user?.id || 'guest'} onLogout={session ? handleLogout : undefined} />
            <ErrorBoundary>
              {session ? (
                <DataProvider userId={session.user.id}>
                  <PullToRefreshWrapper>
                    <Routes>
                    <Route path="/home" element={<LandingPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/support" element={<SupportPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<ArticlePage />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/guide/sm2" element={<SpacedRepetitionGuide />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/howtouse" element={<HowToUsePage />} />
                    <Route element={<AuthenticatedLayout onLogout={handleLogout} />}>
                      <Route path="/" element={<Dashboard deleteModule={deleteModule} userPlan={userPlan} setUserPlan={setUserPlan} />} />
                      <Route path="/create-module" element={<CreateModulePage />} />
                      <Route path="/module/:id" element={<ModulePage deleteModule={deleteModule} />} />
                      <Route path="/module/:id/study-all" element={<StudyAllWordsPage />} />
                      <Route path="/module/:id/learn-unmastered" element={<LearnUnmasteredWordsPage />} />
                      <Route path="/module/:id/learn-all" element={<LearnUnmasteredWordsPage reviewMode="all" />} />
                      <Route path="/module/:id/review-random" element={<LearnUnmasteredWordsPage reviewMode="random" />} />
                      <Route path="/module/:id/review-weak" element={<ReviewWeakWordsPage />} />
                      <Route path="/module/:id/review-forgotten" element={<ReviewForgottenWordsPage />} />
                      <Route path="/module/:id/add-words" element={<AddWordsPage />} />
                      <Route path="/module/:id/learn-topic" element={<LearnTopicPage />} />
                      <Route path="/module/:id/group/:groupId" element={<WordGroupPage />} />
                      <Route path="/module/:id/group/:groupId/flashcard-mode" element={<FlashcardModePage />} />
                      <Route path="/module/:id/group/:groupId/flashcards" element={<FlashcardsPage />} />
                      <Route path="/module/:id/group/:groupId/conversation" element={<ConversationPage />} />
                      <Route path="/module/:id/group/:groupId/practice" element={<SentencePracticePage />} />
                      <Route path="/module/:id/group/:groupId/story" element={<StorySummaryPage />} />
                      <Route path="/module/:id/group/:groupId/mcq-practice" element={<MultipleChoicePracticePage />} />
                      <Route path="/profile" element={<ProfilePage session={session} userPlan={userPlan} />} />
                      <Route path="/classrooms" element={<ClassroomsPage />} />
                      <Route path="/classroom/:classroomId/view" element={<ViewClassPage />} />
                      <Route path="/store" element={<StorePage />} />
                      <Route path="/thank-you" element={<ThankYouPage />} />
                      <Route path="/getting-started" element={<GettingStartedPage />} />
                      <Route path="*" element={<Navigate to="/" />} />
                    </Route>
                    <Route path="/module/demo/group/0" element={<DemoWordGroupPage />} />
                    <Route path="/module/demo/group/0/flashcard-mode" element={<DemoFlashcardModePage />} />
                    <Route path="/module/demo/group/0/flashcards" element={<DemoFlashcardsPage />} />
                    <Route path="/module/demo/group/0/mcq-practice" element={<DemoMCQPracticePage />} />
                    <Route path="/module/demo/group/0/practice" element={<DemoFIBPracticePage />} />
                    <Route path="/module/demo/group/0/flashcards-learning" element={<DemoFlashcardsLearningPage />} />
                    <Route path="/module/demo/group/0/story" element={<DemoStoryPage />} />
                    </Routes>
                  </PullToRefreshWrapper>
                </DataProvider>
              ) : (
                <Routes>
                  <Route path="/home" element={<LandingPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<ArticlePage />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/guide/sm2" element={<SpacedRepetitionGuide />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/howtouse" element={<HowToUsePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/create-password" element={<CreatePasswordPage />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/" element={<LandingPage />} />
                  <Route path="*" element={<Navigate to="/" />} />
                  <Route path="/module/demo/group/0" element={<DemoWordGroupPage />} />
                  <Route path="/module/demo/group/0/flashcard-mode" element={<DemoFlashcardModePage />} />
                  <Route path="/module/demo/group/0/flashcards" element={<DemoFlashcardsPage />} />
                  <Route path="/module/demo/group/0/mcq-practice" element={<DemoMCQPracticePage />} />
                  <Route path="/module/demo/group/0/practice" element={<DemoFIBPracticePage />} />
                  <Route path="/module/demo/group/0/flashcards-learning" element={<DemoFlashcardsLearningPage />} />
                  <Route path="/module/demo/group/0/story" element={<DemoStoryPage />} />
                </Routes>
              )}
            </ErrorBoundary>
          </>
        } />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
